import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { Champ, Etiquette, Saisie, Selection } from './ui/Champ';

/**
 * Rattachement d'une dépense refacturable à un client et à une mission (D4.2).
 *
 * Recherche progressive plutôt qu'une liste déroulante : le CRM compte des
 * centaines de comptes, et personne ne fait défiler une telle liste sur un
 * téléphone.
 *
 * Si le CRM ne répond pas, on le DIT et on laisse enregistrer : perdre une
 * dépense parce qu'un service tiers est indisponible serait absurde, le
 * rattachement peut se faire plus tard.
 */
export function RattachementClient({
  clientId,
  clientNom,
  missionId,
  onChange,
}: {
  clientId: string | null;
  clientNom: string | null;
  missionId: string | null;
  onChange: (v: {
    client_crm_id: string | null;
    client_nom: string | null;
    mission_crm_id: string | null;
    mission_nom: string | null;
    type_mission: string | null;
  }) => void;
}) {
  const [recherche, setRecherche] = useState('');
  const [terme, setTerme] = useState('');

  // On attend une pause de frappe : sans cela, chaque lettre déclenche un
  // appel au CRM, qui est lent et limité en volume.
  useEffect(() => {
    const t = setTimeout(() => setTerme(recherche.trim()), 400);
    return () => clearTimeout(t);
  }, [recherche]);

  const clients = useQuery({
    queryKey: ['ndf', 'crm', 'clients', terme],
    queryFn: () => ndfApi.depenses.clients(terme),
    enabled: terme.length >= 2 && !clientId,
    retry: false,
  });

  const missions = useQuery({
    queryKey: ['ndf', 'crm', 'missions', clientId],
    queryFn: () => ndfApi.depenses.missions(clientId!),
    enabled: Boolean(clientId),
    retry: false,
  });

  const crmIndisponible = clients.isError || missions.isError;

  if (clientId) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Etiquette ton="actif">{clientNom ?? 'Client sélectionné'}</Etiquette>
          <button
            type="button"
            onClick={() =>
              onChange({
                client_crm_id: null,
                client_nom: null,
                mission_crm_id: null,
                mission_nom: null,
                type_mission: null,
              })
            }
            className="min-h-11 text-sm text-nuit underline"
          >
            Changer de client
          </button>
        </div>

        <Champ
          label="Mission"
          requis
          aide={missions.isLoading ? 'Chargement des missions…' : undefined}
        >
          <Selection
            value={missionId ?? ''}
            disabled={missions.isLoading || missions.isError}
            onChange={(e) => {
              const m = (missions.data?.missions ?? []).find((x) => x.id === e.target.value);
              onChange({
                client_crm_id: clientId,
                client_nom: clientNom,
                mission_crm_id: m?.id ?? null,
                mission_nom: m?.nom ?? null,
                // Le type de mission pilote l'imputation analytique : Conseil
                // et Formation ne portent pas le même compte d'achat.
                type_mission: m?.type ?? null,
              });
            }}
          >
            <option value="">Choisir…</option>
            {(missions.data?.missions ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nom}
                {m.type ? ` · ${m.type}` : ''}
              </option>
            ))}
          </Selection>
        </Champ>

        {missions.data?.missions.length === 0 && !missions.isLoading && (
          <p className="text-sm text-nuit/70">
            Aucune mission en cours pour ce client. Les missions terminées, annulées ou en pause ne
            sont pas proposées.
          </p>
        )}
        {crmIndisponible && <AvertissementCrm />}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Champ label="Client" requis>
        <Saisie
          value={recherche}
          placeholder="Tapez les premières lettres…"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          onChange={(e) => setRecherche(e.target.value)}
        />
      </Champ>

      {clients.isLoading && terme.length >= 2 && (
        <p className="text-sm text-nuit/60">Recherche…</p>
      )}
      {crmIndisponible && <AvertissementCrm />}

      <ul className="divide-y divide-nuit/5">
        {(clients.data?.clients ?? []).map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() =>
                onChange({
                  client_crm_id: c.id,
                  client_nom: c.nom,
                  mission_crm_id: null,
                  mission_nom: null,
                  type_mission: null,
                })
              }
              className="min-h-11 w-full text-left text-sm text-nuit"
            >
              {c.nom}
            </button>
          </li>
        ))}
      </ul>

      {terme.length >= 2 && !clients.isLoading && (clients.data?.clients ?? []).length === 0 && (
        <p className="text-sm text-nuit/70">Aucun client ne correspond à cette recherche.</p>
      )}
    </div>
  );
}

function AvertissementCrm() {
  return (
    <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
      La connexion au CRM a échoué : vous pouvez enregistrer la dépense et rattacher le client plus
      tard.
    </p>
  );
}
